/**
 * useFocusTrap — keep keyboard focus inside a modal while it's open.
 *
 * Usage:
 *   const ref = useRef<HTMLDivElement>(null);
 *   useFocusTrap(ref, true);   // active when 2nd arg is true
 *   return <div ref={ref}>…</div>;
 *
 * Behavior:
 *   - On mount, focuses the first tabbable child (or the container).
 *   - Tab cycles through tabbables; Shift+Tab cycles in reverse.
 *   - On unmount, restores focus to whatever was focused before mount.
 *
 * This is intentionally minimal — no library. Modals are short-lived
 * and the tabbable set is small, so re-querying on each Tab is fine.
 */

import { useEffect, type RefObject } from 'react';

const TABBABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getTabbables(root: HTMLElement): HTMLElement[] {
  const list = Array.from(
    root.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR),
  );
  // Filter out anything in a hidden subtree; offsetParent === null
  // catches `display: none` and detached elements.
  return list.filter((el) => el.offsetParent !== null || el === document.activeElement);
}

export function useFocusTrap(
  ref: RefObject<HTMLElement>,
  active: boolean,
): void {
  useEffect(() => {
    if (!active) return;
    const root = ref.current;
    if (!root) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Move focus inside the trap on the next tick so the modal is
    // already mounted and child inputs have settled.
    queueMicrotask(() => {
      const tabbables = getTabbables(root);
      const target = tabbables[0] ?? root;
      target.focus({ preventScroll: true });
    });

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const tabbables = getTabbables(root);
      if (tabbables.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }
      const first = tabbables[0];
      const last = tabbables[tabbables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !root.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    root.addEventListener('keydown', onKey);
    return () => {
      root.removeEventListener('keydown', onKey);
      // Restore focus to whatever triggered the modal so keyboard
      // users land back on familiar ground.
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [ref, active]);
}
